import { Injectable } from '@angular/core';
import {
  FORM_STEP2_MAPPING,
  FORM_STEP3_MAPPING,
  FORM_STEP4_MAPPING,
  FORM_STEP_MAPPING,
  SERVICE_TYPE_MAPPING,
} from '../constants/form-step-mapping.constant';
import { CslWindow } from '../models/csl-window.model';
import { UrlEventTypes } from '../models/url-event.model';

type formStepTypes = 'residential' | 'commercial' | 'dumpster';

import { formatDate } from '@angular/common';
import { ContactMethod, ServiceCode, TrashAndRecyclingFrequency } from '@shared/ui-service';
import { SERVICE_CODE_TO_NAME_MAP } from '../constants/service-code-to-name-map.model';
import { CommercialServiceDetailsFormValues } from '../models/commercial-service-details-form-values.model';
import { DumpsterServiceDetailsFormValues } from '../models/dumpster-service-details-form-values.model';
import { ResidentialServiceDetailsFormValues } from '../models/residential-service-details-form-values.model';
import { ServiceDetailsCachedValues, ServiceItemTracked } from '../models/service-details-cached-values';

@Injectable({ providedIn: 'root' })
export class GTMService {
  private readonly window: CslWindow = window as unknown as CslWindow;
  private enableLogging = false;
  private serviceDetailsCache = {} as ServiceDetailsCachedValues;

  private get dataLayer(): unknown[] {
    return this.window?.dataLayer || [];
  }

  private hasGTM(): boolean {
    return 'dataLayer' in this.window;
  }

  private getFormStepMapping(formStep: formStepTypes): string {
    return FORM_STEP_MAPPING[formStep];
  }

  private getFormStep2Mapping(formStep: formStepTypes): string {
    return FORM_STEP2_MAPPING[formStep];
  }

  private getFormStep3Mapping(formStep: formStepTypes): string {
    return FORM_STEP3_MAPPING[formStep];
  }

  private getFormStep4Mapping(formStep: formStepTypes): string {
    return FORM_STEP4_MAPPING[formStep];
  }

  private formateDate(date: Date | string | undefined): string | null {
    return date ? formatDate(date, 'MMMM d, y', 'en-US') : null;
  }

  private resetServiceDetailsCache(): void {
    this.serviceDetailsCache = {} as ServiceDetailsCachedValues;
  }

  private setCompanyAndBusinessTypeForCommercial(companyName?: string | null, businessType?: string | null): void {
    this.serviceDetailsCache.items = this.serviceDetailsCache.items.map((item) => ({
      ...item,
      ...(companyName ? { item_variant: companyName } : {}),
      ...(businessType ? { item_category5: businessType } : {}),
    }));
  }

  private setPreferredCommunication(preferredCommunication: ContactMethod): void {
    this.serviceDetailsCache = {
      ...this.serviceDetailsCache,
      preferred_communication: ContactMethod[preferredCommunication] ?? null,
    };
  }

  setLogging(enable: boolean): void {
    this.enableLogging = enable;
  }

  pushEvent(event: Record<string, unknown>): void {
    if (!this.hasGTM()) {
      console.warn('GTM is not initialized');
      return;
    }

    if (this.enableLogging) {
      console.log('GTM Event:', event);
    }

    this.dataLayer.push(event);
  }

  clearEvent(): void {
    if (!this.hasGTM()) {
      console.warn('GTM is not initialized');
      return;
    }

    this.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
  }

  urlClickEvent(event: UrlEventTypes, click_text: string, click_url: string): void {
    this.pushEvent({
      event,
      click_text,
      click_url,
    });
  }

  formStep1Event(
    interactionType: 'autofill_click' | 'input_field' | null,
    formStep: formStepTypes,
    address: string | null,
  ): void {
    this.pushEvent({
      event: 'form_step1',
      interaction_type: interactionType,
      form_step: this.getFormStepMapping(formStep),
      text_name: 'surrounding_towns', // Hepsiba Arumugam: suuronding_town here we are capture the link text name
      field_name: address,
    });
  }

  beginCheckoutEvent(itemName: formStepTypes, itemId: ServiceCode, value?: number | null): void {
    const items: ServiceItemTracked[] = [
      {
        item_name: SERVICE_TYPE_MAPPING[itemName],
        item_id: SERVICE_CODE_TO_NAME_MAP[itemId],
        item_variant: null,
        item_category: null,
        item_category2: null,
        item_category3: null,
        item_category4: null,
        item_category5: null,
        item_brand: null,
        quantity: 0,
      },
    ];

    this.clearEvent();

    this.pushEvent({
      event: 'begin_checkout',
      ecommerce: {
        currency: 'USD',
        value: value ?? 0,
        items,
      },
    });

    this.resetServiceDetailsCache();
  }

  formErrorEvent(formStep: formStepTypes, fieldName: string, errorMessage: string): void {
    if (!errorMessage) {
      return;
    }

    this.pushEvent({
      event: 'form_error',
      form_step: this.getFormStepMapping(formStep),
      field_name: fieldName,
      error_message: errorMessage,
    });
  }

  residentialServiceDetailsSelectionEvent(
    serviceCode: ServiceCode,
    values: Partial<ResidentialServiceDetailsFormValues>,
  ) {
    if (!values) return;

    const items = this.getResidentialItems(values, serviceCode);

    this.serviceDetailsCache = {
      value: values.totalPrice ?? 0,
      currency: 'USD',
      items,
    };

    this.clearEvent();

    this.pushEvent({
      event: 'select_item',
      ecommerce: {
        items,
      },
    });
  }

  private getResidentialItems(
    values: Partial<ResidentialServiceDetailsFormValues>,
    serviceCode: ServiceCode,
  ): ServiceItemTracked[] {
    const itemBase = {
      item_name: 'residential services',
      item_variant: null, // not clear spec
      item_id: SERVICE_CODE_TO_NAME_MAP[serviceCode],
      item_category: values.size ? `${values.size} gallons` : null,
      item_category2: this.formateDate(values.startDate),
      item_category3: typeof values.frequency === 'string' ? values.frequency : null,
      item_category4: values.seasonDuration ? `${values.seasonDuration} months` : null,
      quantity: values.quantity ?? null,
      item_brand: null,
      item_category5: null,
    };

    if (values.disposalItems) {
      return values.disposalItems.map((di) => ({
        ...itemBase,
        item_brand: di.item.product,
        quantity: di.quantity,
      }));
    }

    if (serviceCode === ServiceCode.TrashPickUpAndRecyclingTownServiceDetailDto) {
      const recyclingSize = values.recyclingContainerSize ? `${values.recyclingContainerSize} gallons` : null;
      const trashSize = values.trashContainerSize ? `${values.trashContainerSize} gallons` : null;

      const recyclingFrequency =
        values.recyclingFrequency ?? (values.frequency as TrashAndRecyclingFrequency).recyclingFrequency;
      const trashFrequency = values.trashFrequency ?? (values.frequency as TrashAndRecyclingFrequency).trashFrequency;

      return [
        {
          ...itemBase,
          quantity: values.trashQuantity ?? null,
          item_category3: trashFrequency ?? null,
          item_category: `trash ${trashSize}`,
        },
        {
          ...itemBase,
          quantity: values.recyclingQuantity ?? null,
          item_category3: recyclingFrequency ?? null,
          item_category: `recycling ${recyclingSize}`,
        },
      ];
    }

    return [{ ...itemBase }];
  }

  commercialServiceDetailsSelectionEvent(
    serviceCode: ServiceCode,
    values: Partial<CommercialServiceDetailsFormValues>,
    unitOfMeasure?: string | null,
    unitOfMeasureRecycling?: string | null,
  ) {
    if (!values) return;

    const items = this.getCommercialItems(serviceCode, values, unitOfMeasure, unitOfMeasureRecycling);

    this.serviceDetailsCache = {
      value: 0,
      currency: 'USD',
      items,
    };

    this.clearEvent();

    this.pushEvent({
      event: 'select_item',
      ecommerce: {
        items,
      },
    });
  }

  private getCommercialItems(
    serviceCode: ServiceCode,
    values: Partial<CommercialServiceDetailsFormValues>,
    unitOfMeasure: string | null | undefined,
    unitOfMeasureRecycling: string | null | undefined,
  ): ServiceItemTracked[] {
    const itemBase = {
      item_name: 'commercial services',
      item_variant: null, // company name
      item_id: SERVICE_CODE_TO_NAME_MAP[serviceCode],
      item_category: values.trashContainerSize ? `${values.trashContainerSize} ${unitOfMeasure}` : null,
      item_category2: values.startDate ?? null,
      item_category3: values.trashFrequency ?? null,
      item_category4: values.seasonDuration ? `${values.seasonDuration} months` : null,
      item_category5: null, // business type
      item_brand: null,
      quantity: values.trashQuantity ?? null,
    };

    switch (serviceCode) {
      case ServiceCode.CommercialTrashAndRecycling: {
        const recyclingSize = values.recyclingContainerSize
          ? `${values.recyclingContainerSize} ${unitOfMeasureRecycling}`
          : null;
        const trashSize = values.trashContainerSize ? `${values.trashContainerSize} ${unitOfMeasure}` : null;

        return [
          {
            ...itemBase,
            item_category: `trash ${trashSize}`,
            item_category3: values.trashFrequency ?? null,
            quantity: values.trashQuantity ?? null,
          },
          {
            ...itemBase,
            item_category: `recycling ${recyclingSize}`,
            item_category3: values.recyclingFrequency ?? null,
            quantity: values.recyclingQuantity ?? null,
          },
        ];
      }
      case ServiceCode.CommercialRecycling: {
        return [
          {
            ...itemBase,
            item_category: values.recyclingContainerSize
              ? `${values.recyclingContainerSize} ${unitOfMeasureRecycling}`
              : null,
            item_category3: values.recyclingFrequency ?? null,
            quantity: values.recyclingQuantity ?? null,
          },
        ];
      }
      case ServiceCode.CommercialFoodWaste: {
        return [
          {
            ...itemBase,
            item_category: values.foodContainerSize ? `${values.foodContainerSize} ${unitOfMeasure}` : null,
            item_category3: values.foodFrequency ?? null,
            quantity: values.foodQuantity ?? null,
          },
        ];
      }
      case ServiceCode.CommercialBulk: {
        return (values.disposalItems || []).map((di) => ({
          ...itemBase,
          item_brand: di.bulkItem.product,
          quantity: di.bulkItemQuantity,
        }));
      }
    }

    return [itemBase];
  }

  dumpsterServiceDetailsSelectionEvent(values: Partial<DumpsterServiceDetailsFormValues>) {
    if (!values) return;

    const items = this.getDumpsterItems(values);

    this.serviceDetailsCache = {
      value: 0,
      currency: 'USD',
      items,
    };

    this.clearEvent();

    this.pushEvent({
      event: 'select_item',
      ecommerce: {
        ...this.serviceDetailsCache,
      },
    });
  }

  private getDumpsterItems(values: Partial<DumpsterServiceDetailsFormValues>): ServiceItemTracked[] {
    const itemBase = {
      item_name: 'dumpster rental services',
      item_variant: null,
      item_id: 'dumpster rental services',
      item_category: values.container?.name ?? null,
      item_category2: this.formateDate(values.deliveryDate),
      item_category3: values.location ?? null,
      item_category4: values.customerType ?? null, // residential, contractor, commercial
      item_category5:
        this.formateDate(values.estimatedPickupDate) ??
        this.formateDate(values.estimatedDeliveryDate) ??
        values.estimatedServiceDate ??
        null,
      item_brand: (values.material ?? []).map((m) => m.toLowerCase()).join(','),
      quantity: values.quantity ?? 0,
    };

    return [itemBase];
  }

  formStep2Event(formStepTypes: formStepTypes) {
    this.pushEvent({
      event: 'form_step2',
      form_step: this.getFormStep2Mapping(formStepTypes),
    });
  }

  serviceUnavailableEvent(street: string, town: string, zipCode: string, state: string) {
    this.pushEvent({
      event: 'service_unavailable',
      field_name: [street, town, zipCode, state].join(', '),
    });
  }

  formStep3Event(formStepTypes: formStepTypes) {
    this.pushEvent({
      event: 'form_step3',
      form_step: this.getFormStep3Mapping(formStepTypes),
    });
  }

  formStep3ErrorEvent(formStep: formStepTypes, fieldName: string, errorMessage: string): void {
    if (!errorMessage) {
      return;
    }

    this.pushEvent({
      event: 'form_error',
      form_step: this.getFormStep3Mapping(formStep),
      field_name: fieldName,
    });
  }

  addResidentialPaymentInfoEvent(preferredCommunication: ContactMethod): void {
    this.setPreferredCommunication(preferredCommunication); // save preferred communication to cache for later use

    this.clearEvent();

    this.pushEvent({
      event: 'add_payment_info',
      ecommerce: {
        ...this.serviceDetailsCache,
      },
    });
  }

  addCommercialPaymentInfoEvent(
    preferredCommunication: ContactMethod,
    companyName: string,
    businessType: string,
  ): void {
    this.setPreferredCommunication(preferredCommunication); // save preferred communication to cache for later use
    this.setCompanyAndBusinessTypeForCommercial(companyName, businessType);

    this.clearEvent();

    this.pushEvent({
      event: 'add_payment_info',
      ecommerce: {
        ...this.serviceDetailsCache,
      },
    });
  }

  addDumpsterPaymentInfoEvent(companyName?: string | null): void {
    this.setCompanyAndBusinessTypeForCommercial(companyName);

    this.clearEvent();

    this.pushEvent({
      event: 'add_payment_info',
      ecommerce: {
        ...this.serviceDetailsCache,
      },
    });
  }

  dropdownSelectEvent(selectedValue: string, key: string, value: unknown): void {
    this.pushEvent({
      event: 'dropdown_select',
      [key]: value,
      selected_value: selectedValue,
    });
  }

  formStep4Event(formStepTypes: formStepTypes) {
    this.pushEvent({
      event: 'form_step4',
      form_step: this.getFormStep4Mapping(formStepTypes),
    });
  }

  submitOrderEvent(orderId: string) {
    this.clearEvent();

    this.pushEvent({
      event: 'purchase',
      ecommerce: {
        transaction_id: orderId,
        client_id: this.getGA4ClientID(),
        ...this.serviceDetailsCache,
      },
    });
  }

  private getGA4ClientID() {
    const cookie = document.cookie.match(/_ga=(.+?);/);
    if (cookie) {
      const gaCookie = cookie[1].split('.');
      return `${gaCookie[2]}.${gaCookie[3]}`; // Returns the Client ID
    }
    return null;
  }
}
